import { format, formatDistanceToNow, isValid, parse } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { formatInTimeZone } from "date-fns-tz";
import type { ProfileSettings } from "@/lib/account/profile-types";
import { PROFILE_DATE_FORMATS } from "@/lib/account/profile-types";
import { parseDateInput } from "@/lib/date-iso";

const DEFAULT_DATE_FORMAT = PROFILE_DATE_FORMATS[0];

type ProfileDateSettings = Pick<ProfileSettings, "dateFormat" | "languageSetting" | "timeZone">;

export const normalizeProfileDateFormat = (dateFormat: string): string =>
	(PROFILE_DATE_FORMATS as readonly string[]).includes(dateFormat) ? dateFormat : DEFAULT_DATE_FORMAT;

const MIN_TYPED_YEAR = 1000;

/** Typed date: profile pattern first, then ISO / YYYY-MM-DD. Undefined when neither parses. */
export const parseProfileDateInput = (raw: string, dateFormat: string): Date | undefined => {
	const trimmed = raw.trim();
	if (!trimmed) {
		return;
	}
	const typed = parse(trimmed, normalizeProfileDateFormat(dateFormat), new Date());
	if (isValid(typed) && typed.getFullYear() >= MIN_TYPED_YEAR) {
		return typed;
	}
	return parseDateInput(trimmed);
};

export const dateFnsLocale = (language: string): typeof de => (language.startsWith("de") ? de : enUS);

const formatProfileDatePart = (date: Date, settings: ProfileDateSettings): string => {
	const locale = dateFnsLocale(settings.languageSetting);
	return formatInTimeZone(date, settings.timeZone, normalizeProfileDateFormat(settings.dateFormat), { locale });
};

/** Calendar date only (no weekday) in the user's profile timezone and date pattern. */
export const formatProfileCalendarDate = (date: Date, settings: ProfileDateSettings): string => {
	if (Number.isNaN(date.getTime())) {
		return "";
	}
	return formatProfileDatePart(date, settings);
};

/** ISO / YYYY-MM-DD / Date → profile calendar label, or fallback when unparseable. */
export const formatProfileCalendarDateFromInput = (
	value: string | Date | number | null | undefined,
	settings: ProfileDateSettings,
	fallback = "-"
): string => {
	if (value === null || value === undefined || value === "") {
		return fallback;
	}
	const parsed = parseDateInput(value);
	if (!parsed) {
		return typeof value === "string" ? value : fallback;
	}
	return formatProfileCalendarDate(parsed, settings) || fallback;
};

/** Weekday and calendar date in the user's profile timezone and date pattern. */
export const formatProfileDate = (date: Date, settings: ProfileDateSettings): string => {
	if (Number.isNaN(date.getTime())) {
		return "";
	}
	const locale = dateFnsLocale(settings.languageSetting);
	const options = { locale };
	const weekday = formatInTimeZone(date, settings.timeZone, "EEE", options);
	return `${weekday}, ${formatProfileDatePart(date, settings)}`;
};

/** "3 minutes ago" in the profile language. */
export const formatProfileRelative = (date: Date, settings: Pick<ProfileSettings, "languageSetting">): string =>
	Number.isNaN(date.getTime())
		? ""
		: formatDistanceToNow(date, { addSuffix: true, locale: dateFnsLocale(settings.languageSetting) });

/**
 * Wall-clock label: "13:13" or "1:13 PM". The 12h marker is AM/PM in every language
 * (date-fns `de` would give a different marker, so the English locale is used on purpose).
 */
export const formatLocalClock = (
	date: Date,
	options: { readonly showSeconds?: boolean; readonly timeFormat24h: boolean }
): string => {
	const seconds = options.showSeconds ? ":ss" : "";
	return format(date, options.timeFormat24h ? `HH:mm${seconds}` : `h:mm${seconds} a`, { locale: enUS });
};

/** Clock time only, honoring the profile 12/24h preference. */
export const formatProfileTime = (
	date: Date,
	settings: Pick<ProfileSettings, "timeFormat24h" | "timeZone">
): string => {
	if (Number.isNaN(date.getTime())) {
		return "";
	}
	return formatInTimeZone(date, settings.timeZone, settings.timeFormat24h ? "HH:mm" : "h:mm a", { locale: enUS });
};

/** Month-name patterns per profile date pattern: same field order, real month. */
const RANGE_PATTERNS: Record<
	string,
	{ readonly noYear: string; readonly withYear: string; readonly yearFirst: boolean }
> = {
	"dd/MM/yyyy": { withYear: "d MMM yyyy", noYear: "d MMM", yearFirst: false },
	"dd.MM.yyyy": { withYear: "d. MMM yyyy", noYear: "d. MMM", yearFirst: false },
	"MM/dd/yyyy": { withYear: "MMM d, yyyy", noYear: "MMM d", yearFirst: false },
	"yyyy-MM-dd": { withYear: "yyyy MMM d", noYear: "MMM d", yearFirst: true },
};

/**
 * Range label with the real month name, in the profile's field order and language.
 * "MM/dd/yyyy" gives "Jun 1 - Jun 15, 2024", "dd.MM.yyyy" gives "1. Jun - 15. Jun 2024".
 * The shared year is written once: last for day/month-first, first for year-first.
 */
export const formatProfileDateRange = (from: Date, to: Date, settings: ProfileDateSettings): string => {
	const { timeZone } = settings;
	const locale = dateFnsLocale(settings.languageSetting);
	const pattern = RANGE_PATTERNS[normalizeProfileDateFormat(settings.dateFormat)];
	if (!pattern) {
		return "";
	}
	const fmt = (date: Date, tokens: string): string => formatInTimeZone(date, timeZone, tokens, { locale });
	const dayKey = (date: Date): string => fmt(date, "yyyy-MM-dd");
	if (dayKey(from) === dayKey(to)) {
		return fmt(from, pattern.withYear);
	}
	if (dayKey(from).slice(0, 4) !== dayKey(to).slice(0, 4)) {
		return `${fmt(from, pattern.withYear)} - ${fmt(to, pattern.withYear)}`;
	}
	return pattern.yearFirst
		? `${fmt(from, pattern.withYear)} - ${fmt(to, pattern.noYear)}`
		: `${fmt(from, pattern.noYear)} - ${fmt(to, pattern.withYear)}`;
};

/** Date and time in the user's profile timezone, date pattern, and 12/24h preference. */
export const formatProfileDateTime = (
	date: Date,
	settings: Pick<ProfileSettings, "dateFormat" | "languageSetting" | "timeFormat24h" | "timeZone">
): string => {
	if (Number.isNaN(date.getTime())) {
		return "";
	}
	return `${formatProfileDatePart(date, settings)} ${formatProfileTime(date, settings)}`;
};
